import { useState, useEffect } from 'react';
import { Modal, TextInput, Stack, Button, Text, Group, Alert, Badge, MultiSelect } from '@mantine/core';
import { env } from '../utils/env';
import { useUserSearch } from '../hooks/useUserSearch';

interface ShareModalProps {
  type: 'account' | 'budget';
  resourceId: string;
  resourceName: string;
  opened: boolean;
  onClose: () => void;
  onShare: (email: string) => Promise<void>;
  accessToken: string;
}

interface PendingInvite {
  id: string;
  type: 'account' | 'budget';
  resourceName: string;
  from: string;
  date: string;
}

// Update PendingInvites component
const PendingInvites = ({ type, onAccept, onDecline }: {
  type: 'account' | 'budget';
  onAccept: (id: string) => Promise<void>;
  onDecline: (id: string) => Promise<void>;
}) => {
  const [loading, setLoading] = useState(false);
  const [invites, setInvites] = useState<PendingInvite[]>([]);

  useEffect(() => {
    const fetchInvites = async () => {
      try {
        const response = await fetch(`${env.API_URL}/api/shared/${type}s/invites/pending`, {
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch invites');
        const data = await response.json();
        setInvites(data);
      } catch (error) {
        console.error('Error fetching invites:', error);
      }
    };
    fetchInvites();
  }, [type]);

  const handleResponse = async (id: string, accept: boolean) => {
    setLoading(true);
    try {
      await (accept ? onAccept(id) : onDecline(id));
      setInvites(prev => prev.filter(invite => invite.id !== id));
    } catch (error) {
      console.error('Error responding to invite:', error);
    } finally {
      setLoading(false);
    }
  };

  if (invites.length === 0) return null;

  return (
    <Stack mt="md">
      <Text size="sm" fw={500}>Pending Invites</Text>
      {invites.map(invite => (
        <Group key={invite.id} justify="space-between" p="xs" bg="gray.0" style={{ borderRadius: 8 }}>
          <Stack gap={4}>
            <Text size="sm">{invite.resourceName}</Text>
            <Text size="xs" c="dimmed">From: {invite.from}</Text>
          </Stack>
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              color="green"
              onClick={() => handleResponse(invite.id, true)}
              loading={loading}
            >
              Accept
            </Button>
            <Button
              size="xs"
              variant="light"
              color="red"
              onClick={() => handleResponse(invite.id, false)}
              loading={loading}
            >
              Decline
            </Button>
          </Group>
        </Group>
      ))}
    </Stack>
  );
};

export const ShareModal = ({
  type,
  resourceId,
  resourceName,
  opened,
  onClose,
  onShare,
  accessToken,
}: ShareModalProps) => {
  const [email, setEmail] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // user search hook (debounced)
  const { searchUsers, results: userResults, loading: searchLoading } = useUserSearch(accessToken);

  // Convert user search results to Mantine data
  const userOptions = userResults.map(u => {
    let base = (u.display_name || '').trim();
    const uname = (u.username || '').trim();
    if (!base && !uname) base = u.email;
    else if (!base) base = uname;
    const label = uname && base !== uname ? `${base} (@${uname})` : base;
    return { value: u.id.toString(), label };
  });

  const handleShare = async () => {
    if (selectedUserIds.length === 0 && !email.trim()) {
      setError('Please select a user or enter an email');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Share with selected existing users first
      if (selectedUserIds.length > 0) {
        for (const userId of selectedUserIds) {
          const user = userResults.find(u => u.id === userId);
          if (user) {
            await onShare(user.email);
          }
        }
      }

      // Share via email if provided and not already covered by selected user
      if (email.trim()) {
        await onShare(email.trim());
      }

      // Reset inputs
      setEmail('');
      setSelectedUserIds([]);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to share';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (id: string) => {
    const response = await fetch(`${env.API_URL}/api/shared/${type}s/${id}/invites/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ accept: true }),
    });
    if (!response.ok) throw new Error('Failed to accept invite');
  };

  const handleDeclineInvite = async (id: string) => {
    const response = await fetch(`${env.API_URL}/api/shared/${type}s/${id}/invites/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ accept: false }),
    });
    if (!response.ok) throw new Error('Failed to decline invite');
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Share ${type === 'account' ? 'Account' : 'Budget'}`}
      size="md"
    >
      <Stack>
        {error && (
          <Alert color="red" onClose={() => setError(null)} withCloseButton>
            {error}
          </Alert>
        )}

        <Text>
          Share "{resourceName}" with another user
        </Text>

        {/* Search existing users */}
        <MultiSelect
          label="Search Users"
          placeholder="Search by name, username, or email"
          data={userOptions}
          searchable
          maxDropdownHeight={220}
          nothingFoundMessage={searchLoading ? 'Searching...' : 'No users found'}
          onSearchChange={(query) => {
            setSearchQuery(query);
            searchUsers(query);
          }}
          searchValue={searchQuery}
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          disabled={loading}
        />

        <TextInput
          label="Email Address"
          placeholder="Enter email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error}
          disabled={loading}
        />

        <Text size="sm" c="dimmed">
          They will receive an email invitation to access this {type}.
        </Text>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            loading={loading}
            disabled={selectedUserIds.length === 0 && !email.trim()}
          >
            Send Invite
          </Button>
        </Group>

        <PendingInvites
          type={type}
          onAccept={handleAcceptInvite}
          onDecline={handleDeclineInvite}
        />
      </Stack>
    </Modal>
  );
}; 